// Client/src/pages/digi/Invoice/TemplateDesigner/RunsEditor.jsx
//
// Edits a list of text runs (plain text + fields) as one text box where fields
// read as {{Label}}. Typing "{{" opens the field picker at the caret; "Insert
// field" does the same from a button. Below the box, each line / field can be
// made bold, italic, underlined or recoloured, and fields get their format
// (date style, $ sign, decimals).

import React, { useMemo, useRef, useState } from "react";
import FeatherIcon from "feather-icons-react";
import { runsToText, textToRuns } from "./designModel";
import { FieldList, filterFields } from "./FieldPicker";
import { IconButton, SelectInput } from "./controls";

const TOKEN_OPEN = /\{\{([^{}]*)$/;

export default function RunsEditor({ runs, onChange, fieldGroups, labels, kinds, formats, palette, label = "Text", rows = 3, autoFocus, coalesceKey }) {
  const ref = useRef(null);
  const text = useMemo(() => runsToText(runs, labels), [runs, labels]);
  const [menu, setMenu] = useState(null); // { query, start } when the {{ picker is open
  const [active, setActive] = useState(0);
  const [showFormatting, setShowFormatting] = useState(false);

  const filtered = useMemo(() => filterFields(fieldGroups, menu?.query || ""), [fieldGroups, menu]);
  const flat = useMemo(() => filtered.flatMap((g) => g.fields), [filtered]);

  const emit = (nextText) => onChange(textToRuns(nextText, labels, runs), coalesceKey);

  const detectMenu = (value, caret) => {
    const m = TOKEN_OPEN.exec(value.slice(0, caret));
    if (m) {
      setMenu({ query: m[1], start: caret - m[0].length });
      setActive(0);
    } else setMenu(null);
  };

  const insertField = (field) => {
    const el = ref.current;
    const caret = el ? el.selectionStart : text.length;
    const start = menu ? menu.start : caret;
    const token = `{{${field.label}}}`;
    const next = text.slice(0, start) + token + text.slice(menu ? caret : el ? el.selectionEnd : caret);
    emit(next);
    setMenu(null);
    requestAnimationFrame(() => {
      if (!ref.current) return;
      ref.current.focus();
      const pos = start + token.length;
      ref.current.setSelectionRange(pos, pos);
    });
  };

  const setRun = (i, patch) => {
    const next = runs.map((r, j) => {
      if (j !== i) return r;
      const out = { ...r, ...patch };
      Object.keys(out).forEach((k) => (out[k] === undefined || out[k] === false) && k !== "text" && delete out[k]);
      return out;
    });
    onChange(next);
  };

  return (
    <div className="td-runs">
      <div className="td-runs__head">
        <span className="td-label">{label}</span>
        <InsertFieldButton fieldGroups={fieldGroups} onPick={insertField} />
      </div>
      <div className="td-runs__box">
        <textarea
          ref={ref}
          className="td-input td-runs__text"
          rows={rows}
          value={text}
          aria-label={label}
          autoFocus={autoFocus}
          spellCheck
          onChange={(e) => {
            emit(e.target.value);
            detectMenu(e.target.value, e.target.selectionStart);
          }}
          onKeyDown={(e) => {
            if (!menu) return;
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActive((a) => Math.min(flat.length - 1, a + 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActive((a) => Math.max(0, a - 1));
            } else if ((e.key === "Enter" || e.key === "Tab") && flat[active]) {
              e.preventDefault();
              insertField(flat[active]);
            } else if (e.key === "Escape") {
              e.stopPropagation();
              setMenu(null);
            }
          }}
          onBlur={() => setTimeout(() => setMenu(null), 150)}
        />
        {menu && (
          <div className="td-popover td-runs__menu" role="dialog" aria-label="Insert a field">
            <FieldList groups={filtered} onPick={insertField} activeKey={flat[active]?.key} />
          </div>
        )}
      </div>
      <div className="td-hint">Type {"{{"} to insert a field such as {"{{Invoice #}}"}.</div>

      {runs.length > 0 && (
        <button type="button" className="td-linkbtn" aria-expanded={showFormatting} onClick={() => setShowFormatting((s) => !s)}>
          <FeatherIcon icon={showFormatting ? "chevron-down" : "chevron-right"} size={13} /> Formatting per line &amp; field
        </button>
      )}
      {showFormatting && (
        <ul className="td-runs__list">
          {runs.map((r, i) => {
            const isField = r.field !== undefined;
            const kind = isField ? kinds[r.field] : null;
            const fmtOptions =
              kind === "date" ? formats.dateFormats : kind === "money" ? formats.moneyFormats : kind === "number" ? formats.numberFormats : null;
            const preview = isField ? labels[r.field] || r.field : (r.text || "").replace(/\n/g, "↵") || "(empty)";
            return (
              <li key={i} className="td-runs__item">
                <span className={`td-runs__preview${isField ? " is-field" : ""}`} title={preview}>
                  {preview}
                </span>
                <IconButton icon="bold" label="Bold" active={!!r.bold} onClick={() => setRun(i, { bold: !r.bold })} size={13} />
                <IconButton icon="italic" label="Italic" active={!!r.italic} onClick={() => setRun(i, { italic: !r.italic })} size={13} />
                <IconButton icon="underline" label="Underline" active={!!r.underline} onClick={() => setRun(i, { underline: !r.underline })} size={13} />
                <select
                  className="td-input td-runs__color"
                  aria-label="Colour"
                  value={r.color || ""}
                  onChange={(e) => setRun(i, { color: e.target.value || undefined })}
                  style={{ borderLeftColor: r.color ? (palette?.[r.color] || r.color) : undefined }}
                >
                  <option value="">Default colour</option>
                  {Object.keys(palette || {}).map((tk) => (
                    <option key={tk} value={tk}>
                      {tk[0].toUpperCase() + tk.slice(1)}
                    </option>
                  ))}
                  {r.color && !palette?.[r.color] && <option value={r.color}>{r.color}</option>}
                </select>
                {fmtOptions && (
                  <SelectInput
                    ariaLabel={`Format of ${preview}`}
                    value={r.format || ""}
                    placeholder="Default format"
                    options={fmtOptions.map((o) => ({ value: o.id, label: o.label }))}
                    onChange={(v) => setRun(i, { format: v || undefined })}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export function InsertFieldButton({ fieldGroups, onPick, label = "Insert field" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => filterFields(fieldGroups, query), [fieldGroups, query]);
  return (
    <div className="td-insert">
      <button type="button" className="td-btn td-btn--ghost td-btn--sm" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <FeatherIcon icon="plus" size={13} /> {label}
      </button>
      {open && (
        <div className="td-popover td-insert__menu" role="dialog" aria-label="Insert a field" onKeyDown={(e) => e.key === "Escape" && setOpen(false)}>
          <div className="td-search">
            <FeatherIcon icon="search" size={14} />
            <input className="td-input" autoFocus placeholder="Search fields" aria-label="Search fields" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <FieldList
            groups={filtered}
            onPick={(f) => {
              onPick(f);
              setOpen(false);
              setQuery("");
            }}
          />
        </div>
      )}
    </div>
  );
}
